import { listCustomFieldDefs } from '@/features/configuracoes/api';
import { type ContactRow, getContact, updateContact } from '@/features/contatos/api';
import { useContactTags } from '@/features/contatos/hooks';
import { Button } from '@/shared/components/ui/Button';
import { useRefetchOnOpen } from '@/shared/hooks/useRefetchOnOpen';
import { ApiError } from '@/shared/lib/api-client';
import { cn } from '@/shared/lib/cn';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, User, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { patchTicketsDoContato } from '../inbox-debug';
import { anelDoAvatar } from '../lib/anel-do-canal';
import { ImageLightbox } from './ImageLightbox';

interface Props {
  contactId: number;
  open: boolean;
  onClose: () => void;
  /**
   * URL assinada da foto vinda do ticket (signed URL gerada pelo /tickets).
   * `/contacts/:id` retorna só o campo cru `profile_pic_url` que costuma ser
   * NULL — a foto real fica em `profile_pic_key` e precisa de URL assinada
   * gerada com awareness do channel_type. Pra evitar duplicar a lógica de
   * signing aqui, ThreadPanel passa a URL já pronta.
   */
  profilePicUrl?: string | null;
}

/**
 * Modal de edição de contato — uma tela só, sem tabs.
 *
 * Campos (escopo definido pelo user):
 *  - Foto (clicável → ImageLightbox)
 *  - Nome
 *  - Número (read-only — gerenciado em outro fluxo)
 *  - Etiquetas (pills toggleáveis)
 *  - Observações (textarea)
 *  - Campos customizados (grid responsivo conforme custom_field_definitions)
 *
 * Largura 1200px com `max-w-[95vw]` pra responsivo. Atualizar é gated em
 * `isDirty` — compara state atual vs original.
 */
type TabId = 'geral' | 'personalizado';

export function ContactDetailsModal({ contactId, open, onClose, profilePicUrl }: Props) {
  // Foto: prioriza a URL assinada vinda do ticket (prop), com fallback pro
  // campo da resposta de /contacts/:id (geralmente null).
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>('geral');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [customFields, setCustomFields] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);

  const contactQ = useQuery({
    queryKey: ['contact', contactId],
    queryFn: () => getContact(contactId),
    enabled: open,
  });

  // Mesma queryKey do CustomFieldsTab (`['custom-fields']`) — quando o user
  // cria/edita/deleta um campo em Configurações, o invalidate de lá refresca
  // este modal automaticamente. Sem isso, ficaria stale por staleTime.
  const fieldsQ = useQuery({
    queryKey: ['custom-fields'],
    queryFn: listCustomFieldDefs,
    enabled: open,
  });

  // Respeita o scope da tag — modal de contato mostra só `scope=contact|both`.
  // Tags `scope=ticket` (ex: Urgente) ficam só no `+Tag` do header.
  const tagsQ = useContactTags();
  useRefetchOnOpen(open, tagsQ.refetch);
  // Os DADOS do contato também buscam ao abrir, não por prazo. Sem isso a ficha
  // herdava o padrão global (30s): quem removia uma etiqueta em /contatos e
  // abria a ficha em seguida via o estado velho até o prazo vencer. Vale também
  // pra alteração feita por OUTRO atendente, que invalidação local não cobre.
  useRefetchOnOpen(open, contactQ.refetch);

  /**
   * O formulário só é pintado depois que a busca DESTA abertura voltar.
   *
   * Sem isso ele nascia com o dado guardado e se reescrevia quando o atual
   * chegava (a hidratação abaixo depende de `contactQ.data`): a etiqueta
   * piscava marcada→desmarcada na frente do atendente. Pior que o susto: se
   * o dado novo chegasse enquanto ele digitava, a hidratação apagava o texto.
   *
   * O critério é o carimbo AVANÇAR em relação ao capturado na abertura — não
   * `isFetching`, que ainda é false no instante entre abrir e a busca começar
   * (aí o formulário pintaria com o dado velho e o flash voltaria).
   */
  const [carimboNaAbertura, setCarimboNaAbertura] = useState<number | null>(null);
  useEffect(() => {
    setCarimboNaAbertura(open ? contactQ.dataUpdatedAt : null);
    // Só na transição de `open` — incluir dataUpdatedAt aqui recapturaria o
    // carimbo a cada resposta e o formulário nunca seria considerado pronto.
  }, [open]);
  const dadosDestaAbertura =
    carimboNaAbertura !== null &&
    // `errorUpdatedAt` cobre a busca que falhou: sem ele a tela ficaria presa
    // no carregando pra sempre, em vez de cair no bloco de erro abaixo.
    (contactQ.dataUpdatedAt > carimboNaAbertura || contactQ.errorUpdatedAt > carimboNaAbertura);

  // Hidrata o form sempre que o contato carrega.
  useEffect(() => {
    if (!contactQ.data) return;
    setName(contactQ.data.display_name ?? '');
    setNotes(contactQ.data.notes ?? '');
    setTagIds(contactQ.data.tags.map((t) => t.id));
    setCustomFields(contactQ.data.custom_fields ?? {});
    setError(null);
  }, [contactQ.data]);

  useEffect(() => {
    if (!open) {
      setLightboxOpen(false);
      setActiveTab('geral');
      setError(null);
      // Descarta edições não-salvas: ao fechar sem clicar Atualizar, restaura
      // os valores salvos do contato. Sem isso, reabrir o modal mostra o
      // estado dirty (parece "salvo" mas não está). F5 limparia, mas reabrir
      // não — comportamento confuso. Se dado ainda não carregou, mantém vazio.
      const c = contactQ.data;
      if (c) {
        setName(c.display_name ?? '');
        setNotes(c.notes ?? '');
        setTagIds(c.tags.map((t) => t.id));
        setCustomFields(c.custom_fields ?? {});
      }
    }
  }, [open, contactQ.data]);

  // Esc fecha (exceto enquanto lightbox aberto — esse tem seu próprio).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !lightboxOpen) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, lightboxOpen, onClose]);

  const isDirty = useMemo(() => {
    const c = contactQ.data;
    if (!c) return false;
    if ((c.display_name ?? '') !== name) return true;
    if ((c.notes ?? '') !== notes) return true;
    const sortedCurrent = [...tagIds].sort();
    const sortedOriginal = [...c.tags.map((t) => t.id)].sort();
    if (JSON.stringify(sortedCurrent) !== JSON.stringify(sortedOriginal)) return true;
    if (JSON.stringify(customFields) !== JSON.stringify(c.custom_fields ?? {})) return true;
    return false;
  }, [contactQ.data, name, notes, tagIds, customFields]);

  const mut = useMutation({
    mutationFn: () =>
      updateContact(contactId, {
        display_name: name.trim(),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        tag_ids: tagIds,
        custom_fields: customFields,
      }),
    onSuccess: (atualizado) => {
      /**
       * Salvar o contato custava SEIS requisições — flagrado pelo dono no
       * rastro ao mexer numa etiqueta:
       *
       *   1  o PATCH                        (inevitável)
       *   3  as três listas de atendimento
       *   1  o cabeçalho da conversa aberta
       *   1  o contato, rebuscado
       *
       * Eram duas invalidações por PREFIXO: `['ticket']` derruba TODOS os
       * cabeçalhos e `['tickets']` derruba as três listas. Mas deste modal só
       * o NOME vaza pra fora: observações e campos personalizados vivem só
       * aqui, e etiqueta de CONTATO não aparece no card (o card mostra
       * etiqueta do ATENDIMENTO, que é outra tabela). Mexer em etiqueta e
       * pagar por tudo era desproporcional.
       */

      // O PATCH devolve o contato pronto — escreve, não pergunta de novo.
      qc.setQueryData(['contact', contactId], atualizado);

      // A tela de Contatos não está montada aqui, então marcar não busca nada:
      // ela relê quando for aberta.
      void qc.invalidateQueries({ queryKey: ['contacts'] });

      // Só o nome aparece fora deste modal. Se ele não mudou, nada mais no
      // painel está desatualizado — e mexer nas listas seria puro desperdício.
      const nomeAntigo = contactQ.data?.display_name ?? '';
      if (nomeAntigo !== name.trim()) {
        // Encaixa o nome novo nos cards em vez de rebuscar as três listas.
        patchTicketsDoContato(qc, contactId, name.trim());
        // O cabeçalho da conversa também mostra o nome. Só a conversa ABERTA
        // tem cache ativo, então isto custa no máximo uma requisição — e só
        // quando o nome realmente muda.
        void qc.invalidateQueries({ queryKey: ['ticket'] });
      }
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.problem.detail ?? err.problem.title);
      else setError('Falha ao atualizar contato.');
    },
  });

  const submit = (): void => {
    setError(null);
    if (!name.trim()) {
      setActiveTab('geral');
      setError('Informe o nome');
      return;
    }
    mut.mutate();
  };

  if (!open) return null;

  // Enquanto a busca desta abertura não voltar, `contact` fica indefinido — o
  // formulário não pinta e o bloco de carregando abaixo assume.
  const contact: ContactRow | undefined = dadosDestaAbertura ? contactQ.data : undefined;
  const fields = fieldsQ.data ?? [];
  const picUrl = profilePicUrl ?? contact?.profile_pic_url ?? null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-6 backdrop-blur-sm"
        // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click é UX padrão
        onClick={onClose}
      >
        <div
          // biome-ignore lint/a11y/useKeyWithClickEvents: stop propagation só
          onClick={(e) => e.stopPropagation()}
          className="my-8 flex w-[600px] max-w-[95vw] flex-col overflow-hidden rounded-lg border border-n-200 bg-n-0 shadow-lg"
          role="dialog"
          aria-modal="true"
          aria-label="Editar contato"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-n-200 border-b px-6 py-4">
            <h2 className="font-semibold text-base text-n-900">Editar contato</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className="flex h-8 w-8 items-center justify-center rounded-md text-n-500 hover:bg-n-100 hover:text-n-700"
            >
              <X size={18} strokeWidth={1.75} />
            </button>
          </div>

          {/* `isLoading` só cobre a 1ª busca de todas (sem nada guardado). O
              `!dadosDestaAbertura` cobre a reabertura: há dado guardado, mas
              ainda não confirmado nesta abertura — melhor um instante de
              carregando do que pintar algo que muda sozinho em seguida. */}
          {(contactQ.isLoading || !dadosDestaAbertura) && !contactQ.isError && (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={24} className="animate-spin text-n-400" />
            </div>
          )}

          {contactQ.isError && (
            <div className="px-6 py-12 text-center text-danger-600 text-sm">
              Falha ao carregar contato.
            </div>
          )}

          {contact && (
            <>
              {/* Tabs — pattern igual InboxTabs (sem ícones, só texto) */}
              <div className="flex gap-1 border-n-200 border-b px-4">
                {[
                  { id: 'geral' as const, label: 'Geral' },
                  { id: 'personalizado' as const, label: 'Personalizado' },
                ].map((tab) => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={cn(
                        '-mb-px border-b-2 px-3 pt-3 pb-2.5 font-semibold text-sm transition',
                        isActive
                          ? 'border-brand-500 text-brand-600'
                          : 'border-transparent text-n-500 hover:text-n-700',
                      )}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-col gap-6 px-6 py-6">
                {activeTab === 'geral' && (
                  <>
                    {/* Bloco identidade — foto + nome + número */}
                    <div className="flex flex-col items-start gap-6 md:flex-row">
                      <div className="flex flex-col items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => picUrl && setLightboxOpen(true)}
                          disabled={!picUrl}
                          className="group relative flex h-[100px] w-[100px] flex-shrink-0 items-center justify-center overflow-hidden rounded-pill bg-n-100 text-n-400 disabled:cursor-default"
                          style={anelDoAvatar(contact.primary_identifier?.kind === 'ig_id')}
                          aria-label={picUrl ? 'Ampliar foto' : 'Sem foto'}
                        >
                          {picUrl ? (
                            <img
                              src={picUrl}
                              alt={contact.display_name ?? 'Foto do contato'}
                              className="h-full w-full object-cover transition group-hover:scale-105"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <User size={48} strokeWidth={1.25} />
                          )}
                        </button>
                        {picUrl && (
                          <span className="text-[10px] text-n-500">Clique para ampliar</span>
                        )}
                      </div>

                      <div className="flex flex-1 flex-col gap-3">
                        <Field label="Nome*">
                          <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="input-base"
                            placeholder="Nome completo"
                          />
                        </Field>
                        <Field
                          label={
                            contact.primary_identifier?.kind === 'ig_id'
                              ? 'ID do Instagram (não editável)'
                              : 'Número (não editável)'
                          }
                        >
                          <input
                            value={contact.primary_identifier?.value ?? '—'}
                            readOnly
                            className="input-base bg-n-50 font-mono"
                            title="Número não pode ser editado após a criação"
                          />
                        </Field>
                      </div>
                    </div>

                    <Section label="Etiquetas">
                      {(tagsQ.data ?? []).length === 0 ? (
                        <p className="text-n-400 text-sm italic">
                          Nenhuma etiqueta cadastrada. Crie em Configurações → Etiquetas.
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {(tagsQ.data ?? []).map((t) => {
                            const active = tagIds.includes(t.id);
                            return (
                              <button
                                key={t.id}
                                type="button"
                                onClick={() => {
                                  setTagIds((prev) =>
                                    active ? prev.filter((id) => id !== t.id) : [...prev, t.id],
                                  );
                                }}
                                className={cn(
                                  'rounded-pill border px-3 py-1.5 font-semibold text-xs transition',
                                  active
                                    ? 'border-transparent text-white'
                                    : 'border-n-200 bg-n-0 text-n-700 hover:border-brand-300',
                                )}
                                style={active ? { background: t.color } : undefined}
                              >
                                {t.name}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </Section>

                    <Section label="Observações">
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="input-base min-h-[120px] resize-y"
                        placeholder="Notas internas sobre o contato — visível só pra equipe."
                      />
                    </Section>
                  </>
                )}

                {activeTab === 'personalizado' && (
                  <div>
                    {fields.length === 0 ? (
                      <p className="text-n-400 text-sm italic">
                        Nenhum campo customizado configurado. Crie em Configurações → Campos
                        customizados.
                      </p>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {fields.map((f) => (
                          <Field key={f.id} label={`${f.label}${f.required ? '*' : ''}`}>
                            <CustomFieldInput
                              def={f}
                              value={customFields[f.key]}
                              onChange={(v) => setCustomFields((prev) => ({ ...prev, [f.key]: v }))}
                            />
                          </Field>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {error && (
                <div className="border-danger-200 border-t bg-danger-50 px-6 py-2.5 text-danger-700 text-sm">
                  {error}
                </div>
              )}

              {/* Footer */}
              <div className="flex justify-end gap-2 border-n-200 border-t bg-n-50 px-6 py-3">
                <Button variant="ghost" onClick={onClose} disabled={mut.isPending}>
                  Cancelar
                </Button>
                <Button onClick={submit} disabled={!isDirty || mut.isPending}>
                  {mut.isPending && <Loader2 size={12} className="animate-spin" />}
                  Atualizar
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {lightboxOpen && picUrl && (
        <ImageLightbox
          src={picUrl}
          alt={contact?.display_name ?? 'Foto do contato'}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      <style>{`.input-base{display:block;width:100%;border:1px solid var(--n-200);background:var(--bg-surface);border-radius:6px;padding:8px 12px;font-size:13px;color:var(--n-900);outline:none;transition:border-color .15s}.input-base:focus{border-color:var(--brand-500)}.input-base[readonly]{cursor:not-allowed}`}</style>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-semibold text-n-700 text-xs">{label}</span>
      {children}
    </label>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="font-semibold text-n-700 text-xs">{label}</span>
      <div>{children}</div>
    </div>
  );
}

interface CustomFieldDef {
  id: number;
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'enum';
  options: string[];
  required: boolean;
}

function CustomFieldInput({
  def,
  value,
  onChange,
}: {
  def: CustomFieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  if (def.type === 'boolean') {
    return (
      <select
        value={value === true ? 'true' : value === false ? 'false' : ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'true')}
        className="input-base"
      >
        <option value="">—</option>
        <option value="true">Sim</option>
        <option value="false">Não</option>
      </select>
    );
  }
  if (def.type === 'enum') {
    return (
      <select
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="input-base"
      >
        <option value="">—</option>
        {def.options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }
  if (def.type === 'number') {
    return (
      <input
        type="number"
        value={typeof value === 'number' ? value : ((value as string) ?? '')}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        className="input-base"
      />
    );
  }
  if (def.type === 'date') {
    const dateStr = typeof value === 'string' ? value.slice(0, 10) : '';
    return (
      <input
        type="date"
        value={dateStr}
        onChange={(e) => onChange(e.target.value || null)}
        className="input-base"
      />
    );
  }
  return (
    <input
      type="text"
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value || null)}
      className="input-base"
    />
  );
}
